import { create } from 'zustand';

import { toPlayerMessage } from '@/shared/api';
import { isFresh, singleFlight } from '@/shared/lib';

import {
  fetchMonthlyLeaderboard,
  fetchWeeklyLeaderboard,
} from '../api/leaderboard.api';

import type { Leaderboard } from './types';

/**
 * Недельный и месячный рейтинги — с РАЗДЕЛЬНЫМИ флагами.
 *
 * Общий `isLoading` на оба означал, что «Топ 10 игроков» показывает скелет,
 * пока грузится месячная таблица, а ошибка месячного всплывает под
 * недельным списком. Поля держатся плоско, а не вложенным объектом:
 * селектор, собирающий объект, требует `useShallow`, а по одному полю
 * подписка работает сама.
 */
interface LeaderboardStoreState {
  weekly: Leaderboard | null;
  weeklyIsLoading: boolean;
  weeklyError: string | null;
  weeklyLoadedAt: number | null;

  monthly: Leaderboard | null;
  monthlyIsLoading: boolean;
  monthlyError: string | null;
  monthlyLoadedAt: number | null;

  loadWeekly: (options?: { force?: boolean }) => Promise<void>;
  loadMonthly: (options?: { force?: boolean }) => Promise<void>;
  reset: () => void;
}

type Period = 'weekly' | 'monthly';

export const useLeaderboardStore = create<LeaderboardStoreState>()((
  set,
  get,
) => {
  /**
   * Один запрос на период, сколько бы компонентов его ни попросило.
   *
   * Карточка игрока и «Топ 10» монтируются в одном коммите React и оба
   * зовут недельный рейтинг — проверка «уже гружу» тут не помогает,
   * потому что грузить ещё никто не начал.
   */
  const loadPeriod = singleFlight(
    async (period: Period, force: boolean): Promise<void> => {
      const state = get();
      const loadedAt =
        period === 'weekly' ? state.weeklyLoadedAt : state.monthlyLoadedAt;
      if (!force && isFresh(loadedAt)) return;

      set(
        period === 'weekly'
          ? { weeklyIsLoading: true, weeklyError: null }
          : { monthlyIsLoading: true, monthlyError: null },
      );
      try {
        if (period === 'weekly') {
          set({
            weekly: await fetchWeeklyLeaderboard(),
            weeklyLoadedAt: Date.now(),
          });
        } else {
          set({
            monthly: await fetchMonthlyLeaderboard(),
            monthlyLoadedAt: Date.now(),
          });
        }
      } catch (error) {
        const message = toPlayerMessage(error);
        set(
          period === 'weekly'
            ? { weeklyError: message }
            : { monthlyError: message },
        );
      } finally {
        set(
          period === 'weekly'
            ? { weeklyIsLoading: false }
            : { monthlyIsLoading: false },
        );
      }
    },
    (period) => period,
  );

  return {
    weekly: null,
    weeklyIsLoading: false,
    weeklyError: null,
    weeklyLoadedAt: null,

    monthly: null,
    monthlyIsLoading: false,
    monthlyError: null,
    monthlyLoadedAt: null,

    loadWeekly: (options) => loadPeriod('weekly', options?.force ?? false),
    loadMonthly: (options) => loadPeriod('monthly', options?.force ?? false),

    reset: () => {
      set({
        weekly: null,
        weeklyError: null,
        weeklyLoadedAt: null,
        monthly: null,
        monthlyError: null,
        monthlyLoadedAt: null,
      });
    },
  };
});
